import { tokens } from "@blurb/codex-react";
import { createStyles, AnchorProps } from "@mantine/core";
import { RpiLink } from "@rpi/openapi-core";
import { ReactNode } from "react";
import { Link, LinkProps } from "react-router-dom";

const useLinkStyles = createStyles((theme) => ({
  link: {
    fontWeight: theme.other.fontWeights.semibold,
    textDecorationLine: "underline",
    textDecorationStyle: "solid",
    textDecorationSkipInk: "none",
    textUnderlineOffset: "auto",
    textUnderlinePosition: "from-font",
    cursor: "pointer",
    color: theme.other.semanticColors.text.link,
    borderRadius: theme.radius.xs,
    "&:focus-visible": {
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
      outlineOffset: "2px",
    },
    "&:hover": {
      color: theme.other.semanticColors.text.linkHover,
    },
    "&:active": {
      color: theme.other.semanticColors.text.linkActive,
    },
  },
  inline: {
    "&:hover": {
      color: theme.other.semanticColors.text.linkInlineHover,
    },
    "&:active": {
      color: theme.other.semanticColors.text.linkInlineActive,
    },
  },
  disabled: {
    color: tokens.color.semantic.text.disabled,
    cursor: "not-allowed",
    // Repeated for the interactive states so the link styles above can't win on specificity —
    // the click is already inert via the handler guard, so only the colors need overriding.
    "&:hover, &:active": {
      color: tokens.color.semantic.text.disabled,
    },
  },
}));

type RpaOutLinkVariant = "link" | "inline";

/**
 * Adds a router anchor link which adds styles to the current text style.
 * Use within a Text component.
 */
export function RpaNavLink({ className, ...props }: LinkProps) {
  const { classes, cx } = useLinkStyles();
  return <RpiLink component={Link} variant="unstyled" className={cx(classes.link, className)} {...props} />;
}
/**
 * Adds an anchor link which adds styles to the current text style.
 * Use within a Text component.
 */
export function RpaOutLink({
  className,
  variant,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & Omit<AnchorProps, "variant"> & { variant?: RpaOutLinkVariant }) {
  const { classes } = useLinkStyles();

  return (
    <RpiLink
      component={"a"}
      variant="unstyled"
      className={`${classes.link}${variant === "inline" ? ` ${classes.inline}` : ""}${
        className ? ` ${className}` : ""
      }`}
      {...props}
    />
  );
}

export function RpaButtonLink({
  onClick,
  children,
  disabled,
  ...props
}: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "onClick" | "href"> & {
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  children: ReactNode;
  disabled?: boolean;
}) {
  const { classes, cx } = useLinkStyles();
  return (
    <RpiLink
      variant="unstyled"
      className={cx(classes.link, { [classes.disabled]: disabled })}
      aria-disabled={disabled}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (disabled) return;
        onClick(e);
      }}
      // Ideally this would be a button
      href="#"
      {...props}
    >
      {children}
    </RpiLink>
  );
}
